import { isSimilar } from "utils/fuzzyMatch";
import i18n from "locales/i18n";
import { createFormatters } from "locales/format";
import { optionLabel } from "../../helpers/optionLabels";
import type { ChargerEstimatorData } from "pages/chargerEstimator";
import type {
  EvChargerQuoteInput,
  EvChargerServiceRequestInput,
} from "generated/graphql";
import type { GoogleRatingListing } from "components/basic/googleRating";

/**
 * Data contract + builders for the Quote (estimate) page.
 *
 * The Quote page is fully data-driven via {@link QuotePageData} so it can be
 * populated from a mock object during development or from server data in
 * production. To switch sources, swap {@link buildMockQuotePageData} for
 * {@link buildQuotePageDataFromService} (e.g. when navigating from ChargerCard).
 */

// Quote data is built at view time, so these follow the language switcher.
const te = i18n.getFixedT(null, "estimator");
const formatDate = (value: Date | number) =>
  createFormatters(i18n.language).date(value);

/**
 * Fixed vendor-credential labels shown on the action card. These never change
 * (only the values come from the vendor), so they live as constants rather than
 * data fields.
 */
export const QUOTE_REGISTRATION_LABEL = "ECRA/ESA:";
export const QUOTE_INSURANCE_LABEL = "Insurance";

/**
 * Charger setups whose model is auto-selected (cheapest match) upstream in the
 * estimator (ChargerCard). For these the charger model is fixed on the quote:
 * the user cannot change it or choose "None".
 */
export const AUTO_SELECT_CHARGER_SETUPS = [
  "NEMA 14-50 Outlet Only",
  "Tesla Wall hardwired",
];

/** A selectable priced option (charger model or add-on service). */
export interface QuotePriceOption {
  id: number;
  label: string;
  price: number; // dollars; 0 for "None"
}

/** A single description/amount line in the estimate table. */
export interface QuoteLineItem {
  id: string | number;
  label: string;
  price: number; // dollars
}

/** Vendor details shown in the estimate header and the right action card. */
export interface QuoteVendorInfo {
  id: number;
  businessName: string; // Vendor.businessName
  address: string; // Vendor.address (owning account's address)
  email: string; // Vendor.publicEmail; empty when the vendor hides it
  phoneNumber: string; // Vendor.publicPhone; empty when the vendor hides it
  website: string | null; // Vendor.businessWebsite
  logoUrl: string | null; // Vendor.brandLogoURL
  about: string | null; // Vendor.brandAbout
  googleListing: GoogleRatingListing | null; // Vendor.googleListing (live)
  isVerified: boolean; // Vendor.isVerified, set by an admin
  // --- MOCK FIELDS (no backend source on the Vendor entity / query yet) ---
  distanceText: string; // MOCK: could map from EVChargerQuote.distance
  responseText: string; // MOCK: e.g. "Response: ~2 hrs"
  // Labels are fixed constants (QUOTE_REGISTRATION_LABEL / QUOTE_INSURANCE_LABEL);
  // only the values below are data:
  registrationValue: string; // MOCK: e.g. "#7012345"
  insuranceValue: string; // MOCK: e.g. "$2M Liability"
}

/** Customer details (sourced from ChargerEstimatorData on the previous page). */
export interface QuoteCustomerInfo {
  fullName: string;
  address: string;
  phoneNumber: string;
  email: string;
}

/** Customization the user can still change on the quote screen. */
export interface QuoteCustomization {
  chargerModelOptions: QuotePriceOption[]; // from "evseType" service fields
  addOnOptions: QuotePriceOption[]; // from "addOnServices" service fields
  selectedChargerModelId: number;
  selectedAddOnId: number;
  /**
   * When true the charger model is fixed and shown as read-only text instead of
   * a dropdown. This is set for the auto-select setups in
   * {@link AUTO_SELECT_CHARGER_SETUPS} (e.g. "NEMA 14-50 Outlet Only"), where the
   * cheapest matching model is chosen upstream and the user may not change it or
   * pick "None" — mirroring ChargerCard's auto-select behavior.
   */
  isChargerModelLocked: boolean;
}

/** Estimate metadata (numbers, dates, tax). */
export interface QuoteMeta {
  estimateNumber: string; // auto-generated client-side (generateEstimateNumber); ideally backend-issued
  date: string;
  taxRate: number; // e.g. 0.13 for Ontario HST
  taxLabel: string; // e.g. "HST"
  hstNumber: string; // maps to Vendor.businessNumber
  validityText: string; // from EVChargerService.quoteValidFor
}

/**
 * The installation configuration the customer submitted (from
 * ChargerEstimatorData / the persisted EVChargerServiceRequest). Shown read-only
 * on the quote so the customer can review exactly what they entered.
 */
export interface QuoteInstallationDetails {
  address: string;
  installationType: string;
  vehicleType?: string | null;
  houseType: string;
  chargerLocation: string;
  chargerSetup: string;
  electricalPanelLocation: string;
  mainServiceSize: string;
  wireLength: string;
  purchaseType: string;
  elevator: boolean;
  pool: boolean;
  hotTub: boolean;
  additionalInformation?: string | null;
  electricalPanelPhotoURL?: string | null;
  mainBreakerPhotoURL?: string | null;
}

/** Full input contract for the Quote page. */
export interface QuotePageData {
  serviceId: number; // EVChargerService.id — required to submit the quote
  vendor: QuoteVendorInfo;
  customer: QuoteCustomerInfo;
  installation: QuotePriceOption; // installation line (label + cost)
  customization: QuoteCustomization;
  /**
   * Add-ons already fixed on the quote, each its own line item. The vendor sets
   * these when revising; the estimator leaves this empty and offers its single
   * add-on through `customization` instead.
   */
  addOns: QuoteAddOnItem[];
  inclusions: string[];
  assumptions: string[];
  paymentTerms: string | null;
  notes: string[];
  /** Submitted installation configuration (rendered read-only when present). */
  installationDetails?: QuoteInstallationDetails | null;
  meta: QuoteMeta;
}

/** A priced extra stored on the quote (EVChargerQuote.addOns). */
export interface QuoteAddOnItem {
  label: string;
  price: number;
}

/** Add-on names for a one-line summary, e.g. a table cell. */
export const addOnLabels = (addOns?: QuoteAddOnItem[] | null): string =>
  (addOns ?? []).map((addOn) => addOn.label).join(", ");

/**
 * Order-independent identity of an add-on set. Mirrors `addOnSignature` on the
 * server so both sides agree on when two quotes describe the same estimate.
 */
export const addOnSignature = (addOns?: QuoteAddOnItem[] | null): string =>
  (addOns ?? [])
    .map((addOn) => `${addOn.label}:${addOn.price}`)
    .sort()
    .join("|");

/** Computed line items + totals for the estimate. */
export interface QuoteTotals {
  lineItems: QuoteLineItem[];
  subtotal: number;
  tax: number;
  total: number;
}

const NONE_OPTION: QuotePriceOption = { id: 0, label: "None", price: 0 };

/** Resolve a priced option by id, defaulting to the "None" option. */
const findOption = (
  options: QuotePriceOption[],
  id: number,
): QuotePriceOption =>
  options.find((option) => option.id === id) ?? NONE_OPTION;

/**
 * Generate a client-side estimate number in the form YYMMDD-XX (XX random).
 * NOTE: temporary stopgap — the backend should issue a stable, unique estimate
 * number when the quote is persisted (see the BACKEND MIGRATION PLAN below).
 */
export const generateEstimateNumber = (date: Date = new Date()): string => {
  const yy = `${date.getFullYear()}`.slice(-2);
  const mm = `${date.getMonth() + 1}`.padStart(2, "0");
  const dd = `${date.getDate()}`.padStart(2, "0");
  const suffix = `${Math.floor(Math.random() * 100)}`.padStart(2, "0");
  return `${yy}${mm}${dd}-${suffix}`;
};

/**
 * Recompute the estimate line items and totals from the current selections.
 * Called whenever the charger model or add-on selection changes so the
 * displayed totals update immediately.
 */
export const computeQuoteTotals = (
  data: QuotePageData,
  selectedChargerModelId: number,
  selectedAddOnId: number,
): QuoteTotals => {
  const chargerModel = findOption(
    data.customization.chargerModelOptions,
    selectedChargerModelId,
  );
  const addOn = findOption(data.customization.addOnOptions, selectedAddOnId);

  const lineItems: QuoteLineItem[] = [
    { id: "charger", label: chargerModel.label, price: chargerModel.price },
    {
      id: "installation",
      label: data.installation.label,
      price: data.installation.price,
    },
  ];

  // Only show the add-on line when a real (priced) add-on is selected.
  if (addOn.id !== 0 && addOn.price > 0) {
    lineItems.push({ id: "addOn", label: addOn.label, price: addOn.price });
  }

  // Add-ons the vendor fixed on the quote get a line each. Ids are positional
  // so they stay unique alongside the estimator's "addOn" line above.
  data.addOns.forEach((item, index) => {
    lineItems.push({
      id: `addOn-${index}`,
      label: item.label,
      price: item.price,
    });
  });

  const subtotal = lineItems.reduce((sum, item) => sum + item.price, 0);
  const tax = Math.round(subtotal * data.meta.taxRate * 100) / 100;
  const total = subtotal + tax;

  return { lineItems, subtotal, tax, total };
};

/** Used only when a service carries no `quoteValidFor`; vendors always set one. */
const DEFAULT_QUOTE_VALIDITY = "4 weeks";

/** Vendor terms read "1 Week" … "4 Weeks"; the note reads them mid-sentence. */
const validityForTerm = (term?: string | null): string =>
  te("quote.validity_phrase", {
    term: (term ?? DEFAULT_QUOTE_VALIDITY).toLowerCase(),
  });

/**
 * Footer notes for the quote. `validity` completes "This estimate is …", e.g.
 * "valid for 1 week" or "valid until 5/3/2025".
 */
const buildQuoteNotes = (validity: string): string[] => [
  te("quote.footer_note", { validity }),
];

/**
 * Dummy quote data for development / UI work. Mirrors the Ecohive sample
 * estimate. Replace with {@link buildQuotePageDataFromService} once real data
 * is wired through from the previous page (ChargerCard) / server.
 */
export const buildMockQuotePageData = (): QuotePageData => ({
  serviceId: 0, // MOCK: no real EVChargerService in standalone/dev mode
  vendor: {
    id: 1,
    businessName: "Ecohive Inc",
    address: "10 Four Seasons Place, 10th Floor, Toronto, ON M9B 6H7",
    email: "barkybob@gmail.com",
    phoneNumber: "647-997-8267",
    website: "www.ecohive.ca",
    logoUrl: null, // falls back to a business-name heading when null
    about:
      "Family-owned contractor specializing in residential & light-commercial EVSE since 2016. Servicing GTA + Halton.",
    // Never a sample rating: this mock is also the fallback real users see.
    googleListing: null,
    isVerified: false,
    // MOCK fields below (no backend source yet):
    distanceText: "5 km away",
    responseText: "Response: ~2 hrs",
    registrationValue: "#7012345",
    insuranceValue: "$2M Liability",
  },
  customer: {
    fullName: "Bob Baker",
    address: "369 Karenwood Rd, Oakville, ON L3J 6H9",
    phoneNumber: "647-997-8267",
    email: "barkybob@gmail.com",
  },
  installation: {
    id: -1,
    label: "Installation with 30FT Wire",
    price: 1000,
  },
  customization: {
    chargerModelOptions: [
      NONE_OPTION,
      {
        id: 1,
        label: "NEMA 14-50 with Grizzl-E Smart Level 2 Charger",
        price: 950,
      },
      { id: 2, label: "Tesla Wall Connector", price: 609 },
    ],
    addOnOptions: [
      NONE_OPTION,
      { id: 10, label: "Permit & Inspection", price: 180 },
      { id: 11, label: "Sub-panel upgrade", price: 450 },
    ],
    selectedChargerModelId: 1,
    selectedAddOnId: 0,
    isChargerModelLocked: false,
  },
  addOns: [],
  inclusions: [
    "All Materials & Labour",
    "Electrical Inspection & Permit",
    "Complimentary Site Visit",
  ],
  assumptions: [
    "Electrical panel has 2 spare breaker spaces (fuses not accepted)",
    "Work completed weekdays, 8:00 AM – 4:00 PM",
    "Additional charges may apply for fishing cable through an unfinished basement",
    "Electrical service can support EV load (no panel upgrade required)",
    "Indoor cable included; exterior TECK cable and connectors billed separately if required",
  ],
  paymentTerms:
    "Payment terms: 50% deposit, 50% upon completion. Payment can be made by: Credit Card, E-Transfers, Cheque. Financing available.",
  notes: buildQuoteNotes(validityForTerm(DEFAULT_QUOTE_VALIDITY)),
  meta: {
    estimateNumber: generateEstimateNumber(),
    date: "May 12, 2025",
    taxRate: 0.13,
    taxLabel: "HST",
    hstNumber: "797221241-RT0001", // MOCK: Vendor.businessNumber when present
    validityText: `Estimate valid for: ${DEFAULT_QUOTE_VALIDITY}`,
  },
});

/** Minimal shape of a service field as returned from the recommendations query. */
interface ServiceFieldLike {
  id: number;
  fieldLabel: string;
  customLabel?: string | null;
  price?: number | null;
  fieldName: string;
}

/** Minimal shape of the vendor relation on a service. */
interface ServiceVendorLike {
  id?: number | null;
  businessName?: string | null;
  address?: string | null;
  publicEmail?: string | null;
  publicPhone?: string | null;
  businessWebsite?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  businessNumber?: string | null;
  brandLogoURL?: string | null;
  brandAbout?: string | null;
  googleListing?: GoogleRatingListing | null;
  isVerified?: boolean | null;
}

/** Minimal shape of an EVChargerService used to build quote data. */
export interface QuoteServiceSource {
  id: number;
  quoteValidFor?: string | null;
  paymentTerms?: string | null;
  evChargerServiceFields?: ServiceFieldLike[] | null;
  vendor?: ServiceVendorLike | null;
  createdAt?: string | null;
}

/**
 * Payload passed via React Router location state from ChargerCard to the Quote
 * page. It carries the chosen service, the full estimator configuration (used
 * for both display and the backend submission), and the current selections.
 * The Quote page rebuilds {@link QuotePageData} from this with
 * {@link buildQuotePageDataFromService}.
 */
export interface QuoteNavigationState {
  service: QuoteServiceSource;
  chargerConfigData: ChargerEstimatorData;
  /** Omitted when the quote is opened without explicit selections (e.g. the
   * "Next Quote" button), in which case the defaults are derived from the
   * service by {@link buildQuotePageDataFromService}. */
  selected?: { chargerModelId: number; addOnId: number };
  services?: QuoteServiceSource[];
  serviceIndex?: number;
}

/**
 * Server-backed builder. Maps an EVChargerService (+ customer context and the
 * current selections) into {@link QuotePageData}. Fields that do not yet have a
 * backend source reuse the mock placeholders and are flagged as MOCK below.
 */
export const buildQuotePageDataFromService = (
  service: QuoteServiceSource,
  chargerConfigData: ChargerEstimatorData,
  selected?: { chargerModelId?: number; addOnId?: number },
): QuotePageData => {
  const fields = service.evChargerServiceFields ?? [];
  const mock = buildMockQuotePageData(); // reused only for MOCK-only fields

  const { chargerSetup, chargerLocation, wireLength } = chargerConfigData;

  // Charger model options matching the chosen setup + location.
  const matchingChargerModels: QuotePriceOption[] = fields
    .filter(
      (field) =>
        field.fieldName === "evseType" &&
        isSimilar(`${chargerSetup} (${chargerLocation})`, field.fieldLabel, 1),
    )
    .map((field) => ({
      id: field.id,
      label: field.customLabel || field.fieldLabel,
      price: field.price ?? 0,
    }));

  // Auto-select setups lock the charger model: omit "None" so the user cannot
  // deselect it (mirrors ChargerCard's auto-select behavior).
  const isChargerModelLocked =
    AUTO_SELECT_CHARGER_SETUPS.includes(chargerSetup);

  const chargerModelOptions: QuotePriceOption[] = isChargerModelLocked
    ? matchingChargerModels
    : [NONE_OPTION, ...matchingChargerModels];

  // Add-on service options.
  const addOnOptions: QuotePriceOption[] = [
    NONE_OPTION,
    ...fields
      .filter((field) => field.fieldName === "addOnServices")
      .map((field) => ({
        id: field.id,
        label: field.customLabel || field.fieldLabel,
        price: field.price ?? 0,
      })),
  ];

  // Installation cost from the matching cableLength / wireLength field.
  const installationField = fields.find(
    (field) =>
      field.fieldName === "cableLength" &&
      isSimilar(wireLength, field.fieldLabel, 1),
  );

  const inclusions = fields
    .filter((field) => field.fieldName === "inclusions")
    .map((field) => field.fieldLabel);

  const assumptions = fields
    .filter((field) => field.fieldName === "assumptions")
    .map((field) => field.fieldLabel);

  // Default selection: cheapest model when locked (matches ChargerCard), else
  // the first real match, else "None".
  const cheapestChargerModel =
    matchingChargerModels.reduce<QuotePriceOption | null>(
      (cheapest, option) =>
        !cheapest || option.price < cheapest.price ? option : cheapest,
      null,
    );
  const defaultChargerModel = isChargerModelLocked
    ? (cheapestChargerModel ?? NONE_OPTION)
    : (matchingChargerModels[0] ?? NONE_OPTION);

  const resolvedChargerModelId =
    selected?.chargerModelId ?? defaultChargerModel.id;
  // Never leave a locked model on "None" (id 0).
  const selectedChargerModelId =
    isChargerModelLocked && resolvedChargerModelId === 0
      ? defaultChargerModel.id
      : resolvedChargerModelId;

  return {
    serviceId: service.id,
    vendor: {
      id: service.vendor?.id ?? 0,
      businessName: service.vendor?.businessName ?? te("quote.unknown_vendor"),
      address: service.vendor?.address ?? "",
      email: service.vendor?.publicEmail ?? "",
      phoneNumber: service.vendor?.publicPhone ?? "",
      website: service.vendor?.businessWebsite ?? null,
      logoUrl: service.vendor?.brandLogoURL ?? null,
      about: service.vendor?.brandAbout ?? null,
      googleListing: service.vendor?.googleListing ?? null,
      isVerified: Boolean(service.vendor?.isVerified),
      // MOCK fields (no backend source yet) — reuse placeholders:
      distanceText: mock.vendor.distanceText,
      responseText: mock.vendor.responseText,
      registrationValue: mock.vendor.registrationValue,
      insuranceValue: mock.vendor.insuranceValue,
    },
    customer: {
      fullName: chargerConfigData.fullName,
      address: chargerConfigData.address,
      phoneNumber: chargerConfigData.phoneNumber,
      email: chargerConfigData.email,
    },
    installation: {
      id: installationField?.id ?? -1,
      label: installationField
        ? te("quote.installation_with", {
            option: optionLabel(te, installationField.fieldLabel),
          })
        : te("quote.installation_wire", {
            length: optionLabel(te, wireLength),
          }),
      price: installationField?.price ?? 0,
    },
    customization: {
      chargerModelOptions,
      addOnOptions,
      selectedChargerModelId,
      selectedAddOnId: selected?.addOnId ?? 0,
      isChargerModelLocked,
    },
    // The estimator prices its one add-on through `customization`.
    addOns: [],
    inclusions: inclusions.length ? inclusions : mock.inclusions,
    assumptions: assumptions.length ? assumptions : mock.assumptions,
    paymentTerms: service.paymentTerms ?? null,
    // MOCK: footer notes not modeled on the service yet, but the validity
    // sentence tracks the vendor's own quoteValidFor setting.
    notes: buildQuoteNotes(validityForTerm(service.quoteValidFor)),
    installationDetails: {
      address: chargerConfigData.address,
      installationType: chargerConfigData.installationType,
      vehicleType: chargerConfigData.vehicleType,
      houseType: chargerConfigData.houseType,
      chargerLocation: chargerConfigData.chargerLocation,
      chargerSetup: chargerConfigData.chargerSetup,
      electricalPanelLocation: chargerConfigData.electricalPanelLocation,
      mainServiceSize: chargerConfigData.mainServiceSize,
      wireLength: chargerConfigData.wireLength,
      purchaseType: chargerConfigData.purchaseType,
      // Non-null: the preferences page requires an explicit Yes/No selection
      // for each before the estimator flow can reach this point.
      elevator: chargerConfigData.elevator as boolean,
      pool: chargerConfigData.pool as boolean,
      hotTub: chargerConfigData.hotTub as boolean,
      additionalInformation: chargerConfigData.additionalInformation,
      electricalPanelPhotoURL: chargerConfigData.electricalPanelPhotoURL,
      mainBreakerPhotoURL: chargerConfigData.mainBreakerPhotoURL,
    },
    meta: {
      estimateNumber: generateEstimateNumber(), // client-side; backend should issue this
      date: service.createdAt
        ? formatDate(parseInt(service.createdAt))
        : mock.meta.date,
      taxRate: 0.13,
      taxLabel: te("quote.hst"),
      hstNumber: service.vendor?.businessNumber ?? mock.meta.hstNumber,
      validityText: service.quoteValidFor
        ? te("quote.valid_for", { term: service.quoteValidFor })
        : mock.meta.validityText,
    },
  };
};

/* -------------------------------------------------------------------------- */
/*  Backend submission                                                        */
/* -------------------------------------------------------------------------- */

/** Inputs for the `createServiceChargeQuote` mutation. */
export interface QuoteSubmissionInputs {
  serviceRequestInput: EvChargerServiceRequestInput;
  quoteInput: EvChargerQuoteInput;
}

/**
 * Map the current quote (page data + live selections + the originating
 * estimator config) into the `createServiceChargeQuote` mutation inputs. Run
 * this when the user presses "Book a Call".
 *
 * Server-owned values are intentionally NOT sent from here:
 *  - subtotal / taxAmount / taxRate / taxLabel / total: the backend recomputes
 *    these from the cost components to prevent client tampering.
 *  - estimateNumber: the backend issues a unique, stable value.
 *
 * Client-supplied descriptive snapshots (inclusions, assumptions, notes,
 * paymentTerms) and the cost components (chargerCost, installationCost,
 * addOnServiceCost) are sent and persisted as-is.
 *
 * Photos (electricalPanelPhoto / mainBreakerPhoto) must be uploaded to S3 first
 * (see useS3 / ensurePhotoUrls in the Quote page) so their *PhotoURL values are
 * populated on `chargerConfigData` before calling this.
 */
export const buildQuoteSubmissionInputs = (
  data: QuotePageData,
  chargerConfigData: ChargerEstimatorData,
  selectedChargerModelId: number,
  selectedAddOnId: number,
  distanceTextOverride?: string,
): QuoteSubmissionInputs => {
  const totals = computeQuoteTotals(
    data,
    selectedChargerModelId,
    selectedAddOnId,
  );
  const chargerModel = findOption(
    data.customization.chargerModelOptions,
    selectedChargerModelId,
  );
  const addOn = findOption(data.customization.addOnOptions, selectedAddOnId);
  const hasAddOn = addOn.id !== 0 && addOn.price > 0;

  const serviceRequestInput: EvChargerServiceRequestInput = {
    address: chargerConfigData.address,
    // Sent so the backend can re-check the vendor's service radius itself.
    latitude: chargerConfigData.latitude,
    longitude: chargerConfigData.longitude,
    installationType: chargerConfigData.installationType,
    vehicleType: chargerConfigData.vehicleType,
    houseType: chargerConfigData.houseType,
    chargerLocation: chargerConfigData.chargerLocation,
    chargerSetup: chargerConfigData.chargerSetup,
    electricalPanelLocation: chargerConfigData.electricalPanelLocation,
    mainServiceSize: chargerConfigData.mainServiceSize,
    electricalPanelPhotoURL: chargerConfigData.electricalPanelPhotoURL,
    mainBreakerPhotoURL: chargerConfigData.mainBreakerPhotoURL,
    wireLength: chargerConfigData.wireLength,
    purchaseType: chargerConfigData.purchaseType,
    // Non-null: the preferences page requires an explicit Yes/No selection
    // for each before the estimator flow can reach this point.
    elevator: chargerConfigData.elevator as boolean,
    pool: chargerConfigData.pool as boolean,
    hotTub: chargerConfigData.hotTub as boolean,
    additionalInformation: chargerConfigData.additionalInformation,
  };

  const quoteInput: EvChargerQuoteInput = {
    evChargerServiceId: data.serviceId,
    chargerModel: chargerModel.label,
    chargerCost: chargerModel.price,
    addOnService: hasAddOn ? addOn.label : undefined,
    addOnServiceCost: hasAddOn ? addOn.price : undefined,
    installationCost: data.installation.price,
    // totalCost is sent for the existing contract, but the backend recomputes
    // the authoritative subtotal/tax/total from the cost components.
    totalCost: totals.total,
    distance: distanceTextOverride ?? data.vendor.distanceText, // fallback to existing value when no live coordinates are available
    stage: "Requested",
    userAccepted: false,
    // Descriptive snapshots of what the customer saw (persisted as-is):
    inclusions: data.inclusions,
    assumptions: data.assumptions,
    notes: data.notes,
    paymentTerms: data.paymentTerms ?? undefined,
    // NOTE: subtotal, taxAmount, taxRate, taxLabel, total and estimateNumber are
    // owned and computed by the backend (not sent from the client).
  };

  return { serviceRequestInput, quoteInput };
};

/* -------------------------------------------------------------------------- */
/*  Persisted-quote → QuotePageData (read-only detail view)                   */
/* -------------------------------------------------------------------------- */

/** Vendor relation as returned on a persisted quote (GetQuotesByAccount). */
interface QuoteRecordVendorLike {
  id?: number | null;
  businessName?: string | null;
  address?: string | null;
  publicEmail?: string | null;
  publicPhone?: string | null;
  businessWebsite?: string | null;
  businessNumber?: string | null;
  brandLogoURL?: string | null;
  brandAbout?: string | null;
  googleListing?: GoogleRatingListing | null;
  isVerified?: boolean | null;
}

/** Service-request relation as returned on a persisted quote. */
interface QuoteRecordServiceRequestLike {
  address?: string | null;
  installationType?: string | null;
  vehicleType?: string | null;
  houseType?: string | null;
  chargerLocation?: string | null;
  chargerSetup?: string | null;
  electricalPanelLocation?: string | null;
  mainServiceSize?: string | null;
  wireLength?: string | null;
  purchaseType?: string | null;
  elevator?: boolean | null;
  pool?: boolean | null;
  hotTub?: boolean | null;
  additionalInformation?: string | null;
  electricalPanelPhotoURL?: string | null;
  mainBreakerPhotoURL?: string | null;
}

/**
 * Minimal shape of a persisted EVChargerQuote (as returned by
 * GetQuotesByAccount) needed to rebuild {@link QuotePageData} for the read-only
 * detail view.
 */
export interface QuoteRecordSource {
  estimateNumber: string;
  distance: string;
  chargerModel: string;
  chargerCost: number;
  addOns?: QuoteAddOnItem[] | null;
  installationCost: number;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  taxLabel: string;
  totalCost: number;
  inclusions?: string[] | null;
  assumptions?: string[] | null;
  notes?: string[] | null;
  paymentTerms?: string | null;
  createdAt?: string | null;
  expiresAt?: string | null;
  isExpired?: boolean | null;
  evChargerService?: {
    id?: number | null;
    quoteValidFor?: string | null;
    vendor?: QuoteRecordVendorLike | null;
  } | null;
  chargerServiceRequest?: QuoteRecordServiceRequestLike | null;
}

/** Customer contact for a persisted quote (from the logged-in account / me). */
export interface QuoteCustomerContact {
  fullName: string;
  email: string;
  phoneNumber: string;
}

/** A quote's expiry, or null when it carries none. */
const quoteExpiry = (quote: { expiresAt?: string | null }): Date | null => {
  // Epoch milliseconds, not ISO.
  const expiry = quote.expiresAt ? new Date(Number(quote.expiresAt)) : null;

  return expiry && !Number.isNaN(expiry.getTime()) ? expiry : null;
};

/**
 * Whether a persisted quote has lapsed. `expiresAt` wins over `isExpired`,
 * which is derived server-side and so goes stale between fetches.
 */
export const isQuoteExpired = (quote: {
  expiresAt?: string | null;
  isExpired?: boolean | null;
}): boolean => {
  const expiry = quoteExpiry(quote);

  return expiry ? expiry.getTime() < Date.now() : (quote.isExpired ?? false);
};

/**
 * Validity line for a persisted quote: its real expiry date, phrased for
 * whether that date has passed. Falls back to the caller's default when the
 * quote carries no expiry.
 */
const quoteValidityText = (
  quote: QuoteRecordSource,
  fallback: string,
): string => {
  const expiry = quoteExpiry(quote);

  if (!expiry) {
    return quote.evChargerService?.quoteValidFor
      ? te("quote.valid_for", { term: quote.evChargerService.quoteValidFor })
      : fallback;
  }

  return isQuoteExpired(quote)
    ? te("quote.expired_on", { date: formatDate(expiry) })
    : te("quote.valid_until", { date: formatDate(expiry) });
};

/** Same validity, phrased to complete "This estimate is …" in the footer note. */
const quoteNoteValidity = (quote: QuoteRecordSource): string => {
  const expiry = quoteExpiry(quote);

  if (!expiry) {
    return validityForTerm(quote.evChargerService?.quoteValidFor);
  }

  return isQuoteExpired(quote)
    ? te("quote.validity_expired", { date: formatDate(expiry) })
    : te("quote.validity_until", { date: formatDate(expiry) });
};

/**
 * Build {@link QuotePageData} from a persisted quote for the read-only detail
 * view shown in the dashboard. The stored cost breakdown is reproduced exactly:
 * a single (locked) charger-model option and an optional add-on option, so
 * {@link computeQuoteTotals} recomputes the same subtotal/tax/total that was
 * persisted (taxRate is taken from the record).
 */
export const buildQuotePageDataFromQuote = (
  quote: QuoteRecordSource,
  customer: QuoteCustomerContact,
): QuotePageData => {
  const mock = buildMockQuotePageData(); // reused only for MOCK-only vendor fields
  const vendor = quote.evChargerService?.vendor;
  const request = quote.chargerServiceRequest;

  const chargerOption: QuotePriceOption = {
    id: 1,
    label: quote.chargerModel,
    price: quote.chargerCost,
  };
  return {
    serviceId: quote.evChargerService?.id ?? 0,
    vendor: {
      id: vendor?.id ?? 0,
      businessName: vendor?.businessName ?? te("quote.unknown_vendor"),
      address: vendor?.address ?? "",
      email: vendor?.publicEmail ?? "",
      phoneNumber: vendor?.publicPhone ?? "",
      website: vendor?.businessWebsite ?? null,
      logoUrl: vendor?.brandLogoURL ?? null,
      about: vendor?.brandAbout ?? null,
      googleListing: vendor?.googleListing ?? null,
      isVerified: Boolean(vendor?.isVerified),
      // MOCK fields (no backend source yet) — reuse placeholders:
      distanceText: quote.distance || mock.vendor.distanceText,
      responseText: mock.vendor.responseText,
      registrationValue: mock.vendor.registrationValue,
      insuranceValue: mock.vendor.insuranceValue,
    },
    customer: {
      fullName: customer.fullName,
      address: request?.address ?? "",
      phoneNumber: customer.phoneNumber,
      email: customer.email,
    },
    installation: {
      id: -1,
      label: request?.wireLength
        ? te("quote.installation_wire", {
            length: optionLabel(te, request.wireLength),
          })
        : te("quote.installation"),
      price: quote.installationCost,
    },
    customization: {
      chargerModelOptions: [chargerOption],
      // Saved add-ons are a fixed list, not a selection, so the dropdown is
      // empty and they come through `addOns` below instead.
      addOnOptions: [NONE_OPTION],
      selectedChargerModelId: chargerOption.id,
      selectedAddOnId: 0,
      isChargerModelLocked: true,
    },
    addOns: (quote.addOns ?? []).map((addOn) => ({
      label: addOn.label,
      price: addOn.price,
    })),
    inclusions: quote.inclusions ?? [],
    assumptions: quote.assumptions ?? [],
    paymentTerms: quote.paymentTerms ?? null,
    // Rebuilt rather than replayed: the stored snapshot repeats the vendor's
    // validity term from creation time, while the note should state the
    // quote's own expiry.
    notes: buildQuoteNotes(quoteNoteValidity(quote)),
    installationDetails: request
      ? {
          address: request.address ?? "",
          installationType: request.installationType ?? "",
          vehicleType: request.vehicleType ?? null,
          houseType: request.houseType ?? "",
          chargerLocation: request.chargerLocation ?? "",
          chargerSetup: request.chargerSetup ?? "",
          electricalPanelLocation: request.electricalPanelLocation ?? "",
          mainServiceSize: request.mainServiceSize ?? "",
          wireLength: request.wireLength ?? "",
          purchaseType: request.purchaseType ?? "",
          elevator: request.elevator ?? false,
          pool: request.pool ?? false,
          hotTub: request.hotTub ?? false,
          additionalInformation: request.additionalInformation ?? null,
          electricalPanelPhotoURL: request.electricalPanelPhotoURL ?? null,
          mainBreakerPhotoURL: request.mainBreakerPhotoURL ?? null,
        }
      : null,
    meta: {
      estimateNumber: quote.estimateNumber,
      date: quote.createdAt
        ? new Date(parseInt(quote.createdAt)).toLocaleDateString()
        : mock.meta.date,
      taxRate: quote.taxRate,
      taxLabel: quote.taxLabel,
      hstNumber: vendor?.businessNumber ?? mock.meta.hstNumber,
      // A saved quote has a real expiry date, so show that rather than the
      // vendor's generic "valid for N weeks" setting.
      validityText: quoteValidityText(quote, mock.meta.validityText),
    },
  };
};
